// @flow

import {
  AppInstallPermission,
  MarketplaceProduct,
  StargateAPI,
} from '@atlassian/mpac-services-client';
import { foldMarketplaceProduct } from '@atlassian/mpac-site-selection';
import { MarketplaceAppPaymentModelValues } from '@atlassian/mpac-types/dist/GraphQLGateway.gql.types';
import queryString from 'query-string';
import { useEffect, useState } from 'react';

import { STARGATE_BASE_PATH } from '../../../../../../../constants';

import type { CompatibleProduct } from '@atlassian/mpac-app-listing';
import type {
  MarketplaceProduct as MarketplaceProductType,
  SelectedSiteAndProduct,
} from '@atlassian/mpac-site-selection';
import type { AppDetailsLink } from '@atlassian/mpac-types';
import type { MarketplaceAppPaymentModel } from '@atlassian/mpac-types/dist/GraphQLGateway.gql.types';

type JiraConfluenceCompatibleProduct = {
  ...CompatibleProduct,
  id: MarketplaceProductType,
};

export const onlyJiraConfluenceProducts = (
  compatibleProducts: $ReadOnlyArray<CompatibleProduct>
): JiraConfluenceCompatibleProduct[] => // $FlowFixMe - Array#filter doesn't refine types https://github.com/facebook/flow/issues/1414
  compatibleProducts.filter(product =>
    [MarketplaceProduct.JIRA, MarketplaceProduct.CONFLUENCE].includes(product.id)
  );

export type DisambiguationNotNeeded = {|
  furtherDisambiguationNeeded: false,
  disambiguationResult: {
    site: $PropertyType<SelectedSiteAndProduct, 'site'>,
    product: $PropertyType<SelectedSiteAndProduct, 'product'>,
  },
|};

export type DisambiguationNeeded = {|
  furtherDisambiguationNeeded: true,
  disambiguationResult: null,
|};

export type DisambiguateSiteAndProduct = (
  MarketplaceProductType[],
  MarketplaceAppPaymentModel
) => Promise<DisambiguationNeeded | DisambiguationNotNeeded>;

export const disambiguateSiteAndProduct: DisambiguateSiteAndProduct = async (
  compatibleProducts,
  paymentModel
) => {
  const stargate = new StargateAPI(STARGATE_BASE_PATH);

  // 1. Needed if an app is compatible with multiple products
  if (compatibleProducts.length > 1) {
    return { furtherDisambiguationNeeded: true, disambiguationResult: null };
  }

  const availableSites = await stargate.marketplace.availableSites(compatibleProducts);

  // 2. Needed if a user has multiple cloud sites
  if (availableSites.sites.length !== 1) {
    return { furtherDisambiguationNeeded: true, disambiguationResult: null };
  }

  const [onlyAvailableSite] = availableSites.sites;
  const [onlyProduct] = compatibleProducts;

  // 3. Site should have a compatible product
  const compatibleProductsIntersection = compatibleProducts.filter(product =>
    onlyAvailableSite.products.includes(product)
  );

  if (compatibleProductsIntersection.length !== 1) {
    return { furtherDisambiguationNeeded: true, disambiguationResult: null };
  }

  // 4. User should have appropriate permission on this site
  const permission = await stargate.marketplace.appInstallPermission(
    onlyProduct,
    onlyAvailableSite.cloudId
  );

  const canInstallApp =
    paymentModel === MarketplaceAppPaymentModelValues.Free
      ? permission !== AppInstallPermission.CANNOT_INSTALL
      : permission === AppInstallPermission.CAN_INSTALL_FREE_AND_PAID;

  if (canInstallApp) {
    return {
      furtherDisambiguationNeeded: false,
      disambiguationResult: {
        site: onlyAvailableSite,
        product: onlyProduct,
      },
    };
  } else {
    return {
      furtherDisambiguationNeeded: true,
      disambiguationResult: null,
    };
  }
};

export type DisambiguationResult = DisambiguationNotNeeded | DisambiguationNeeded;
type UseDisambiguatedSiteAndProduct = (
  boolean,
  MarketplaceProductType[],
  MarketplaceAppPaymentModel
) => DisambiguationResult;

export const useDisambiguatedSiteAndProduct: UseDisambiguatedSiteAndProduct = (
  userLoggedIn,
  compatibleProducts,
  paymentModel
) => {
  const [siteProductDisambiguation, setSiteProductDisambiguation] = useState<DisambiguationResult>({
    furtherDisambiguationNeeded: true,
    disambiguationResult: null,
  });

  useEffect(
    () => {
      if (userLoggedIn) {
        disambiguateSiteAndProduct(compatibleProducts, paymentModel)
          .then(result => {
            setSiteProductDisambiguation(result);
          })
          .catch(console.error.bind(console));
      }
    },
    [userLoggedIn]
  );

  return siteProductDisambiguation;
};

export const getMailToLink = (appName: string, appKey: string, appDetailsLink: AppDetailsLink) => {
  const subject = encodeURIComponent(`Check out this great Atlassian Marketplace app: ${appName}`);
  const body = encodeURIComponent(
    `Hello favorite admin,\n\nI was browsing Atlassian Marketplace and found an app I think is useful for our organisation: ${appName}.\n\nYou can check it out here: ${appDetailsLink.expand(
      { appKey }
    )}\n\nThanks!`
  );
  return `mailto:?subject=${subject}&body=${body}`;
};

const getEmceeAppListingPageLink = (
  appKey: string,
  { site, product }: SelectedSiteAndProduct,
  isForEndUser: boolean,
  additionalQueryParams: {| installDialogOpen?: boolean, requestDialogOpen?: boolean |}
) => {
  const queryParamString = queryString.stringify({ source: 'mpac', ...additionalQueryParams });
  const deeplink = `app/${appKey}?${queryParamString}`;
  const pageFragment = isForEndUser ? 'enduser-discover' : 'discover';

  return `${site.url}${foldMarketplaceProduct(product)(
    () => '/plugins/servlet/ac/com.atlassian.jira.emcee',
    () => '/wiki/plugins/servlet/ac/com.atlassian.confluence.emcee'
  )}/${pageFragment}#!/discover/${deeplink}`;
};

export const getEmceeAppListingInstallLink = (
  appKey: string,
  siteAndProduct: SelectedSiteAndProduct
) => {
  return getEmceeAppListingPageLink(appKey, siteAndProduct, false, { installDialogOpen: true });
};

export const getEmceeAppListingRequestAppLink = (
  appKey: string,
  siteAndProduct: SelectedSiteAndProduct
) => {
  return getEmceeAppListingPageLink(appKey, siteAndProduct, true, { requestDialogOpen: true });
};
